"use client";

import {
  Container,
  Title,
  Group,
  Button,
  Stack,
  Text,
  Box,
  ColorSwatch,
  Badge,
  Card,
} from "@mantine/core";
import { NavigationProgress, setNavigationProgress } from "@mantine/nprogress";
import { IconArrowRight, IconRefresh } from "@tabler/icons-react";
import { useState, useEffect, useRef } from "react";
import QuizPhotoUpload from "@/components/QuizPhotoUpload";
import TypeformQuiz, { QuizQuestion } from "@/components/TypeformQuiz";
import { trackQuizStart, trackQuizComplete, trackCtaClick } from "@/lib/analytics";
import EmailResultsCapture from "@/components/EmailResultsCapture";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";

function getQuizCount(): number {
  const baseCount = 34521;
  const baseDate = new Date("2025-01-01").getTime();
  const now = Date.now();
  const daysSinceBase = Math.floor((now - baseDate) / (1000 * 60 * 60 * 24));
  return baseCount + daysSinceBase * 58;
}

// Questions array is now created within the component to access translations

function determineMetal(answers: Record<string, string>, t: any) {
  let score = 0;

  // Undertone (most significant)
  if (answers.undertone === "warm") score += 3;
  if (answers.undertone === "cool") score -= 3;
  if (answers.undertone === "neutral") score += 0.5;

  // Sun reaction
  if (answers.sun === "always_tans" || answers.sun === "tans") score += 1;
  if (answers.sun === "burns") score -= 1;

  // Hair color
  const warmHair = ["red", "light_brown"];
  const coolHair = ["dark", "gray", "blonde"];
  if (warmHair.includes(answers.hair)) score += 1;
  if (coolHair.includes(answers.hair)) score -= 0.5;

  // Eye color
  const warmEyes = ["dark_brown", "light_brown"];
  const coolEyes = ["blue", "green", "gray"];
  if (warmEyes.includes(answers.eyes)) score += 0.5;
  if (coolEyes.includes(answers.eyes)) score -= 0.5;

  // Direct preference (strong signal)
  if (answers.preference === "gold") score += 2;
  if (answers.preference === "silver") score -= 2;

  if (score > 2) {
    return {
      metal: "gold",
      confidence: "high",
      description: t("results.goldDesc"),
    };
  } else if (score < -2) {
    return {
      metal: "silver",
      confidence: "high",
      description: t("results.silverDesc"),
    };
  } else {
    return {
      metal: "both",
      confidence: score > 0 ? "slightly_warm" : "slightly_cool",
      description: t("results.bothDesc"),
    };
  }
}

export default function GoldOrSilverJewelryTest() {
  const t = useTranslations("goldOrSilverTest");
  const [quizState, setQuizState] = useState<"intro" | "quiz" | "results">("intro");
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [showStickyButton, setShowStickyButton] = useState(false);
  const mainCtaRef = useRef<HTMLButtonElement>(null);

  const questions: QuizQuestion[] = [
    {
      id: "undertone",
      title: t("questions.undertoneTitle"),
      description: t("questions.undertoneDescription"),
      helpText: t("questions.undertoneHelp"),
      options: [
        { label: t("questions.undertoneWarm"), value: "warm" },
        { label: t("questions.undertoneCool"), value: "cool" },
        { label: t("questions.undertoneNeutral"), value: "neutral" },
        { label: t("questions.undertoneNotSure"), value: "unsure" },
      ],
    },
    {
      id: "sun",
      title: t("questions.sunTitle"),
      description: t("questions.sunDescription"),
      helpText: t("questions.sunHelp"),
      options: [
        { label: t("questions.sunBurns"), value: "burns" },
        { label: t("questions.sunBurnsTans"), value: "burns_tans" },
        { label: t("questions.sunTans"), value: "tans" },
        { label: t("questions.sunAlways"), value: "always_tans" },
      ],
    },
    {
      id: "hair",
      title: t("questions.hairTitle"),
      description: t("questions.hairDescription"),
      helpText: t("questions.hairHelp"),
      options: [
        { label: t("questions.hairDark"), value: "dark", color: "#1a1a1a" },
        { label: t("questions.hairMedium"), value: "medium_brown", color: "#4A3728" },
        { label: t("questions.hairLight"), value: "light_brown", color: "#8B7355" },
        { label: t("questions.hairBlonde"), value: "blonde", color: "#DAA520" },
        { label: t("questions.hairRed"), value: "red", color: "#A52A2A" },
        { label: t("questions.hairGray"), value: "gray", color: "#808080" },
      ],
    },
    {
      id: "eyes",
      title: t("questions.eyesTitle"),
      description: t("questions.eyesDescription"),
      helpText: t("questions.eyesHelp"),
      options: [
        { label: t("questions.eyesDarkBrown"), value: "dark_brown", color: "#3B1F1F" },
        { label: t("questions.eyesLightBrown"), value: "light_brown", color: "#8B7355" },
        { label: t("questions.eyesGreen"), value: "green", color: "#2F4F4F" },
        { label: t("questions.eyesBlue"), value: "blue", color: "#4169E1" },
        { label: t("questions.eyesGray"), value: "gray", color: "#808080" },
      ],
    },
    {
      id: "preference",
      title: t("questions.preferenceTitle"),
      description: t("questions.preferenceDescription"),
      helpText: t("questions.preferenceHelp"),
      options: [
        { label: t("questions.preferenceGold"), value: "gold" },
        { label: t("questions.preferenceSilver"), value: "silver" },
        { label: t("questions.preferenceBoth"), value: "both" },
        { label: t("questions.preferenceNotSure"), value: "unsure" },
      ],
    },
  ];

  useEffect(() => {
    if (quizState !== "intro") return;

    const handleScroll = () => {
      if (mainCtaRef.current) {
        const rect = mainCtaRef.current.getBoundingClientRect();
        setShowStickyButton(rect.bottom < 0);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [quizState]);

  const startQuiz = () => {
    setQuizState("quiz");
    setNavigationProgress(0);
    trackQuizStart("Gold or Silver Test");
  };

  const handleQuizComplete = (quizAnswers: Record<string, string>) => {
    setAnswers(quizAnswers);
    setQuizState("results");
    setNavigationProgress(100);
    trackQuizComplete("Gold or Silver Test");
  };

  const handleQuizExit = () => {
    setQuizState("intro");
    setNavigationProgress(0);
  };

  const resetQuiz = () => {
    setQuizState("intro");
    setAnswers({});
    setNavigationProgress(0);
  };

  // ============================================
  // RESULTS VIEW
  // ============================================
  if (quizState === "results") {
    const result = determineMetal(answers, t);

    return (
      <Container size="md" py="xl">
        <NavigationProgress size={4} />

        <Stack gap="lg" align="center">
          <Card withBorder p="xl" radius="md" w="100%">
            <Stack gap="lg" ta="center" align="center">
              <Badge size="lg" color={result.metal === "silver" ? "gray" : "yellow"} variant="light">
                {t("results.badge")}
              </Badge>
              <Title order={2} c={result.metal === "silver" ? "gray.7" : "yellow.7"}>
                {result.metal === "both" ? t("results.both") : result.metal === "gold" ? t("results.gold") : t("results.silver")}
              </Title>
              <Group gap="xs" justify="center">
                {result.metal === "both" ? (
                  <>
                    <ColorSwatch color="#FFD700" size={32} />
                    <ColorSwatch color="#C0C0C0" size={32} />
                  </>
                ) : (
                  <ColorSwatch color={result.metal === "gold" ? "#FFD700" : "#C0C0C0"} size={32} />
                )}
              </Group>
              <Text size="sm" c="dimmed">
                {result.description}
              </Text>

              <Box w="100%" style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }} />

              <QuizPhotoUpload quizName="Gold or Silver Test" />

              <Box w="100%">
                <EmailResultsCapture
                  source="gold-or-silver-test"
                  undertone={result.metal === "gold" ? "warm" : result.metal === "silver" ? "cool" : "neutral"}
                  resultLabel={
                    result.metal === "both" ? t("results.both") : result.metal === "gold" ? t("results.gold") : t("results.silver")
                  }
                />
              </Box>
            </Stack>
          </Card>

          <Card withBorder p="xl" radius="md" w="100%">
            <Stack gap="sm" ta="center" align="center">
              <Title order={3}>{t("results.bridgeHeading")}</Title>
              <Text size="sm" c="dimmed">
                {t("results.bridgeBody")}
              </Text>
              <Button
                component={Link}
                href="/ai-color-analysis?src=bridge_gsjt"
                color="wumpus"
                rightSection={<IconArrowRight size="1rem" />}
                onClick={() => trackCtaClick("bridge_gsjt_ca")}
              >
                {t("results.bridgeButton")}
              </Button>
            </Stack>
          </Card>

          <Card withBorder p="xl" radius="md" w="100%">
            <Stack gap="sm" ta="center" align="center">
              <Title order={3}>See it on your own face</Title>
              <Text size="sm" c="dimmed">
                Upload a selfie and our free virtual draping tool wraps you in gold vs silver, cream vs white, and warm vs cool red drapes — the same test analysts do in person. Free, no signup.
              </Text>
              <Button
                component={Link}
                href="/virtual-color-draping?src=bridge_gsjt"
                variant="outline"
                color="wumpus"
                rightSection={<IconArrowRight size="1rem" />}
                onClick={() => trackCtaClick("bridge_gsjt_vd")}
              >
                Drape yourself free
              </Button>
            </Stack>
          </Card>

          <Button variant="subtle" onClick={resetQuiz} leftSection={<IconRefresh size="1rem" />} color="gray" size="sm">
            {t("results.takeAgain")}
          </Button>
        </Stack>
      </Container>
    );
  }

  // ============================================
  // QUIZ VIEW (Typeform-style)
  // ============================================
  if (quizState === "quiz") {
    return (
      <>
        <NavigationProgress size={4} color="wumpus" />
        <TypeformQuiz
          questions={questions}
          onComplete={handleQuizComplete}
          onExit={handleQuizExit}
          accentColor="#EAB308" // yellow/gold
        />
      </>
    );
  }

  // ============================================
  // INTRO VIEW
  // ============================================
  return (
    <>
      <Container size="md" py="xl">
        <NavigationProgress size={4} />

        <Stack gap="md" ta="center" maw={500} mx="auto">
          <Title order={1}>{t("intro.title")}</Title>

          <Text c="dimmed">{t("intro.duration")}</Text>

          <Button
            ref={mainCtaRef}
            size="xl"
            onClick={startQuiz}
            rightSection={<IconArrowRight size="1.2rem" />}
            color="wumpus"
            fullWidth
          >
            {t("intro.button")}
          </Button>

          <Text size="xs" c="dimmed">
            {t("intro.noSignup", { count: getQuizCount().toLocaleString() })}
          </Text>
        </Stack>

        <Stack gap="md" mt="xl">
          <Text>
            {t("description.text")}
          </Text>
          <Box>
            <Text mb="xs">{t("description.learnTitle")}</Text>
            <Stack gap="xs" ml="md">
              <Text>• {t("description.learn1")}</Text>
              <Text>• {t("description.learn2")}</Text>
              <Text>• {t("description.learn3")}</Text>
              <Text>• {t("description.learn4")}</Text>
            </Stack>
          </Box>
        </Stack>

        <Stack gap="xl" mt="xl">
          <Box>
            <Title order={2} size="h3" mb="sm">{t("content.q1")}</Title>
            <Text c="dimmed">
              {t("content.a1")}
            </Text>
          </Box>

          <Box>
            <Title order={2} size="h3" mb="sm">{t("content.q2")}</Title>
            <Text c="dimmed">
              {t("content.a2")}
            </Text>
            <Stack gap="sm" mt="sm">
              <Text c="dimmed"><strong>{t("content.a2Vein")}</strong></Text>
              <Text c="dimmed"><strong>{t("content.a2Jewelry")}</strong></Text>
              <Text c="dimmed"><strong>{t("content.a2Undertone")}</strong></Text>
            </Stack>
          </Box>

          <Box ta="center" py="md">
            <Button
              size="lg"
              onClick={startQuiz}
              rightSection={<IconArrowRight size="1rem" />}
              color="wumpus"
              variant="light"
            >
              {t("intro.button")}
            </Button>
          </Box>

          <Box>
            <Title order={2} size="h3" mb="sm">{t("content.q3")}</Title>
            <Text c="dimmed">
              {t("content.a3Rose")}
            </Text>
            <Text c="dimmed" mt="sm">
              {t("content.a3White")}
            </Text>
          </Box>

          <Box>
            <Title order={2} size="h3" mb="sm">{t("content.q4")}</Title>
            <Text c="dimmed">
              {t("content.a4")}
            </Text>
          </Box>

          {/* Related Tools */}
          <Box>
            <Title order={2} size="h3" mb="sm">Related Color & Style Tools</Title>
            <Stack gap="sm">
              <Text c="dimmed">
                <strong><Link href="/skin-tone-test" style={{ color: "#EAB308" }}>Skin Tone Test</Link>:</strong> Dive deeper into understanding your undertone with the vein test, jewelry test, and more DIY methods.
              </Text>
              <Text c="dimmed">
                <strong><Link href="/best-colors-for-skin-tone" style={{ color: "#EAB308" }}>Best Colors for Your Skin Tone</Link>:</strong> Discover which clothing and makeup colors complement your undertone.
              </Text>
              <Text c="dimmed">
                <strong><Link href="/undertone-quiz" style={{ color: "#EAB308" }}>Undertone Quiz</Link>:</strong> Not sure if you're warm, cool, or neutral? Take our quick undertone quiz.
              </Text>
              <Text c="dimmed">
                <strong><Link href="/style-personality-quiz" style={{ color: "#EAB308" }}>Style Personality Quiz</Link>:</strong> Discover your fashion archetype—Classic, Romantic, Dramatic, Natural, Creative, or Minimalist.
              </Text>
            </Stack>
          </Box>
        </Stack>
      </Container>

      {showStickyButton && (
        <Box
          hiddenFrom="md"
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            padding: "0.75rem 1rem",
            background: "white",
            borderTop: "1px solid #eee",
            zIndex: 100,
          }}
        >
          <Button
            size="md"
            onClick={startQuiz}
            rightSection={<IconArrowRight size="1rem" />}
            color="wumpus"
            fullWidth
          >
            {t("intro.button")}
          </Button>
        </Box>
      )}
    </>
  );
}
